import { supabase } from './supabaseClient';
import type { UnbookedSentReportRow } from './unbookedReportsLogic';
import { berichtSchluessel, ladeStilleProjekte, ladeTagGedeckteBerichte } from './projectQuietService';

/**
 * Datenbankzugriff fuer „versendeter Bericht ohne Leistungsbuchung"
 * (Sicht `public.v_unbooked_sent_reports` und RPC
 * `public.set_report_billing_exempt` — supabase/unbooked_sent_reports.sql,
 * MIGRATIONS.md Nr. 45). Die einzige Stelle, die mit beiden redet.
 *
 * Die REGELN liegen daneben in `unbookedReportsLogic.ts` und sind ohne
 * Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist.
 *
 * Hausregel wie im uebrigen Repo: LESEN IST FAIL-OPEN, SCHREIBEN NICHT.
 * Fehlt die Sicht (Migration noch nicht gefahren), liefert das Lesen eine
 * leere Liste statt zu werfen — die Meldungsliste zeigt dann eben nichts
 * Neues, statt die ganze Ansicht mitzureissen. Das Setzen der Ausnahme meldet
 * jeden Fehler dagegen sichtbar: still zu scheitern ist hier besonders teuer,
 * weil der Nutzer sonst glaubt, den Fall erledigt zu haben.
 */

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeSicht = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};

/**
 * Die offenen Faelle.
 *
 * KEIN Projektfilter in der Abfrage, aus zwei Gruenden:
 *
 *  a) Die Sicht laeuft mit `security_invoker = on` und zeigt ohnehin nur
 *     Projekte, die der angemeldete Nutzer sehen darf. Ein zweiter Filter
 *     hier waere Zierde und keine Trennung — und der Versuch, eine
 *     Zugriffsregel im Client „zur Sicherheit" nachzubauen, hat im Repo schon
 *     zwei Wahrheiten fuer dieselbe Frage erzeugt.
 *  b) `.in('project_id', [...])` haengt jede Kennung an die URL. Bei einem
 *     Verwalter mit dreistelliger Projektzahl reisst das die Laengengrenze.
 *
 * Die Ergebnismenge ist klein — es ist eine Mangelliste, keine Datenmenge.
 * Wer nur einen Ausschnitt anzeigen will, filtert das Ergebnis.
 */
export const fetchUnbookedSentReports = async (): Promise<UnbookedSentReportRow[]> => {
    try {
        const { data, error } = await supabase.from('v_unbooked_sent_reports').select('*');
        if (error) throw error;
        const rows = (data || []) as UnbookedSentReportRow[];

        // Archivierte Projekte ohne offene Forderung melden nicht mehr.
        // Gefiltert HIER und nicht in der Sicht: die Regel hat damit EINE
        // Stelle und laesst sich ohne Migration zuruecknehmen. Die Sicht
        // bleibt vollstaendig — wer den Bestand pruefen will, fragt sie
        // unveraendert ab.
        const still = await ladeStilleProjekte(rows.map(r => r.project_id));
        const uebrig = still.size === 0 ? rows : rows.filter(r => !still.has(r.project_id));

        // Tochter mit TAGESPAUSCHALE: hat die Mutter den Begehungstag schon
        // gebucht, ist die Leistung abgerechnet — der Riegel der Sicht sucht
        // dort einen `RPT-CHILD-`-Eintrag, den der richtige Ablauf in diesem
        // Fall nie schreibt. Siehe `istTagGedeckt`.
        const gedeckt = await ladeTagGedeckteBerichte(uebrig);
        return gedeckt.size === 0
            ? uebrig
            : uebrig.filter(r => !gedeckt.has(berichtSchluessel(r.project_id, r.inspection_number)));
    } catch (e) {
        if (istFehlendeSicht(e)) {
            console.warn(
                'v_unbooked_sent_reports fehlt — supabase/unbooked_sent_reports.sql (Nr. 45) noch nicht gefahren.',
            );
            return [];
        }
        console.warn('Offene Berichte ohne Buchung konnten nicht geladen werden:', e);
        return [];
    }
};

/**
 * „Nicht abrechenbar" setzen bzw. zuruecknehmen.
 *
 * Ueber die RPC und NICHT per direktem UPDATE auf `report_history`: die
 * Policies dieser Tabelle stammen aus dem Dashboard und stehen nirgends im
 * Repo. Fehlt dort eine UPDATE-Policy, aendert ein Direkt-Update unter RLS
 * still null Zeilen und meldet KEINEN Fehler — der Nutzer klickt, die Aufgabe
 * bleibt stehen, und niemand erfaehrt warum. Genau diese Klasse stiller
 * Nicht-Wirkung ist der Anlass des ganzen Vorgangs.
 *
 * Der Handelnde (`billing_exempt_by`) kommt in der RPC aus `auth.uid()` und
 * wird hier bewusst NICHT mitgegeben.
 *
 * Wirft bei Fehlschlag weiter — der Aufrufer zeigt den Fehler an.
 */
export const setReportBillingExempt = async (
    reportHistoryId: string,
    exempt: boolean,
    reason: string | null,
): Promise<void> => {
    const { error } = await supabase.rpc('set_report_billing_exempt', {
        p_report_history_id: reportHistoryId,
        p_exempt: exempt,
        p_reason: reason,
    });
    if (error) throw error;
};
